<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import Monitor from "@/services/Monitor";

export default {
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Customer Statistics",
      items: [
        { text: "Customer Statistics", active: true }
      ],
      showLoader: false,
      stats: null
    };
  },
  computed: {

    totalsUsers() {
      if (!this.stats || !this.stats.total || !this.stats.total.users_by_gender) {
        return {
          total: 0,
          male: 0,
          female: 0,
          unknown: 0,
          malePerc: 0,
          femalePerc: 0,
          unknownPerc: 0
        };
      }

      const total = this.stats.total.total_users || 0;
      let male = 0, female = 0, unknown = 0;

      this.stats.total.users_by_gender.forEach(row => {
        if (row.gender === 'M') male = row.total;
        else if (row.gender === 'F') female = row.total;
        else unknown += row.total;
      });

      return {
        total,
        male,
        female,
        unknown,
        malePerc: total ? ((male / total) * 100).toFixed(1) : 0,
        femalePerc: total ? ((female / total) * 100).toFixed(1) : 0,
        unknownPerc: total ? ((unknown / total) * 100).toFixed(1) : 0
      };
    },

    filteredTenants() {
      if (!this.stats) return [];
      return Object.keys(this.stats)
          .filter(key => key !== 'total')
          .map(key => ({ key, value: this.stats[key] }));
    }
  },
  methods: {
    async userStatPerTenant() {
      this.showLoader = true;
      try {
        const response = await Monitor.userStatPerTenant();
        this.stats = response.data;
      } catch (error) {
        console.error("Error fetching customer stats:", error);
      } finally {
        this.showLoader = false;
      }
    },

    formatNumber(value) {
      return value?.toLocaleString() || 0;
    },

    formatPerc(value) {
      return value ? `${value}%` : '0%';
    },

    getGenderCount(tenant, gender) {
      if (!tenant || !tenant.users_by_gender) return 0;
      if (gender === 'unknown') {
        return tenant.users_by_gender
            .filter(u => u.gender !== 'M' && u.gender !== 'F')
            .reduce((sum, u) => sum + u.total, 0);
      }
      const found = tenant.users_by_gender.find(u => u.gender === gender);
      return found ? found.total : 0;
    },

    getGenderPerc(tenant, gender) {
      const total = tenant.total_users || 0;
      const count = this.getGenderCount(tenant, gender);
      return total ? ((count / total) * 100).toFixed(1) + '%' : '0%';
    },

    calculatePercentage(count, otherCount) {
      const total = count + otherCount;
      if (total === 0) return 0;
      return total ? ((count / total) * 100).toFixed(1) + '%' : '0%';
    },

    async exportToExcel() {
      try {
          if (!this.stats) {
              this.failedmsg('No data available for generating the report.');
              return;
          }

          const ExcelJS = require('exceljs');
          const workbook = new ExcelJS.Workbook();

          // Create worksheet for All Customers
          const allCustomersSheet = workbook.addWorksheet('User Statistics');

          // Add main title for All Users section
          allCustomersSheet.addRow(['Total Users']);
          const titleRow = allCustomersSheet.lastRow;
          titleRow.font = { bold: true, size: 14 };
          titleRow.alignment = { horizontal: 'center' };
          allCustomersSheet.mergeCells(`A${titleRow.number}:H${titleRow.number}`);

          // Define column headers for All Customers
          const allCustomersHeaders = [
              { key: 'tenant', header: 'Tenant', width: 30 },
              { key: 'male', header: 'Male', width: 15 },
              { key: 'female', header: 'Female', width: 15 },
              { key: 'unknown', header: 'Unknown', width: 15 },
              { key: 'total', header: 'Total', width: 15 },
              { key: 'male_percentage', header: 'Male %', width: 15 },
              { key: 'female_percentage', header: 'Female %', width: 15 },
              { key: 'unknwon_percentage', header: 'Unknown %', width: 15 }
          ];

          // Add headers to All Customers sheet
          allCustomersSheet.addRow(allCustomersHeaders.map(column => column.header));
          allCustomersSheet.lastRow.font = { bold: true };
          allCustomersSheet.lastRow.alignment = { horizontal: 'center' };

          // Set column widths
          allCustomersHeaders.forEach((column, index) => {
              allCustomersSheet.getColumn(index + 1).width = column.width;
          });

          // Add data rows for All Customers
          this.filteredTenants.forEach(item => {
              if (item.key !== 'TOTAL') {

                  const tenantKey = item.key;       // "ALM"
                  const tenantData = item.value;    // { total_users, users_by_gender }

                  const maleCount = this.getGenderCount(tenantData, 'M');
                  const femaleCount = this.getGenderCount(tenantData, 'F');
                  const unknownCount = this.getGenderCount(tenantData, 'unknown');

                  allCustomersSheet.addRow([
                      tenantKey,
                      maleCount,
                      femaleCount,
                      unknownCount,
                      maleCount + femaleCount + unknownCount,
                      this.calculatePercentage(maleCount, femaleCount + unknownCount),
                      this.calculatePercentage(femaleCount, maleCount + unknownCount),
                      this.calculatePercentage(unknownCount, maleCount + femaleCount)
                  ]);
              }
          });

          // Add Total row for All Customers
          const totalRow1 = allCustomersSheet.addRow([
              'TOTAL',
              this.totalsUsers.male,
              this.totalsUsers.female,
              this.totalsUsers.unknown,
              this.totalsUsers.total,
              this.totalsUsers.malePerc + '%',
              this.totalsUsers.femalePerc + '%',
              this.totalsUsers.unknownPerc + '%'
          ]);
          totalRow1.font = { bold: true };

          // Add spacing between sections
          allCustomersSheet.addRow([]);

          // Style the entire sheet
          allCustomersSheet.eachRow((row, rowNumber) => {
              if (rowNumber > 1) { // Skip header rows
                  row.alignment = { horizontal: 'center' };
              }
          });

          // Create a blob from the Excel workbook
          const buffer = await workbook.xlsx.writeBuffer();

          // Create a Blob object and initiate download
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `Customers_Statistics_${new Date().toISOString().slice(0, 10)}.xlsx`;
          link.click();

      } catch (error) {
          console.error('Error exporting to Excel:', error);
          this.failedmsg('An error occurred while generating the Excel report.');
      }
    },
    
  },
  mounted() {
    this.userStatPerTenant();
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">

            <!-- Loader -->
            <div v-if="showLoader" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-2">Loading customer statistics...</p>
            </div>

            <!-- Stats content -->
            <div v-if="stats && stats.total">
              <!-- Export Button -->
              <div class="row">
                <div class="col-12 text-end">
                  <button @click="exportToExcel" class="btn btn-success">
                    <i class="fas fa-file-excel me-2"></i> Export to Excel
                  </button>
                </div>
              </div>

              <!-- Summary card -->
              <div class="row mb-4">
                <div class="col-xl-6 col-md-6 mb-4">
                  <div class="card border-left-primary shadow h-100 py-2">
                    <div class="card-body">

                      <div class="row no-gutters align-items-center">
                        <div class="col mr-2">
                          <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                            Total Customers
                          </div>
                          <div class="h5 mb-0 font-weight-bold text-gray-800">
                            {{ formatNumber(totalsUsers.total) }}
                          </div>
                          <div class="mt-2 text-xs">
                                                    <span class="font-weight-bold">
                                                        <i class="fas fa-male me-1"></i> Male: {{ formatNumber(totalsUsers.male) }} ({{ formatPerc(totalsUsers.malePerc) }})
                                                    </span>
                            <span class="font-weight-bold ms-2">
                                                        <i class="fas fa-female me-1"></i> Female: {{ formatNumber(totalsUsers.female) }} ({{ formatPerc(totalsUsers.femalePerc) }})
                                                    </span>
                            <span class="text-danger font-weight-bold ms-2">
                                                        <i class="fas fa-user-alt me-1"></i> Unknown: {{ formatNumber(totalsUsers.unknown) }} ({{ formatPerc(totalsUsers.unknownPerc) }})
                                                    </span>
                          </div>
                        </div>
                        <div class="col-auto">
                          <i class="fas fa-mobile-screen fa-2x text-gray-300"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tenant table -->
              <div class="row">
                <div class="col-md-12">
                  <div class="card mb-3">
                    <div class="card-body">
                      <h4 class="card-title mb-3">Customers per Tenant</h4>
                      <div class="table-responsive">
                        <table class="table table-centered table-hover mb-0">
                          <thead>
                          <tr>
                            <th>Tenant</th>
                            <th class="text-end">Male</th>
                            <th class="text-end">Female</th>
                            <th class="text-end">Unknown</th>
                            <th class="text-end">Total</th>
                            <th class="text-end">Male %</th>
                            <th class="text-end">Female %</th>
                            <th class="text-end">Unknown %</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="tenantObj in filteredTenants" :key="tenantObj.key">
                            <td>{{ tenantObj.key }}</td>
                            <td class="text-end">{{ formatNumber(getGenderCount(tenantObj.value, 'M')) }}</td>
                            <td class="text-end">{{ formatNumber(getGenderCount(tenantObj.value, 'F')) }}</td>
                            <td class="text-end">{{ formatNumber(getGenderCount(tenantObj.value, 'unknown')) }}</td>
                            <td class="text-end">{{ formatNumber(tenantObj.value.total_users) }}</td>
                            <td class="text-end">{{ getGenderPerc(tenantObj.value, 'M') }}</td>
                            <td class="text-end">{{ getGenderPerc(tenantObj.value, 'F') }}</td>
                            <td class="text-end">{{ getGenderPerc(tenantObj.value, 'unknown') }}</td>
                          </tr>
                           <tr class="table-active">
                                <th>Total</th>
                                <th class="text-end">{{ formatNumber(totalsUsers.male) }}</th>
                                <th class="text-end">{{ formatNumber(totalsUsers.female) }}</th>
                                <th class="text-end">{{ formatNumber(totalsUsers.unknown) }}</th>
                                <th class="text-end">{{ formatNumber(totalsUsers.total) }}</th>
                                <th class="text-end">{{ formatPerc(totalsUsers.malePerc) }}%</th>
                                <th class="text-end">{{ formatPerc(totalsUsers.femalePerc) }}%</th>
                                <th class="text-end">{{ formatPerc(totalsUsers.unknownPerc) }}%</th>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
